import { CallConnectionDto, CallDto } from '@act/shared/data-transfer-objects';
import { timeFormatter } from '@act/shared/utilities';
import { Goal } from 'stories/containers/GoalsTracking/types';
import { ReemaThemeV2 } from '@act/storybook';

export const SCROLLBAR_STYLES = {
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[225],
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[300],
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[400],
    },
  },
};

export const getGoalDisplayData = (
  goal: Goal
): { title: string; dueDate: string } => {
  let title: string;
  let dueDate: string;

  switch (goal.goalType) {
    case 'consent':
      title = 'name' in goal ? goal.name : 'Consent';
      dueDate = null;
      break;
    case 'need':
      title = 'issueLabel' in goal ? goal.issueLabel || 'Unnamed need' : 'Need';
      dueDate =
        'dueDate' in goal && goal.dueDate
          ? new Date(goal.dueDate as string | number | Date).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
              }
            )
          : 'No due date';
      break;
    case 'survey':
      title =
        'survey' in goal
          ? `${goal.survey?.displayName || 'Survey'} survey`
          : 'Survey';
      dueDate =
        'dueAt' in goal && goal.dueAt
          ? new Date(goal.dueAt as string | number | Date).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
              }
            )
          : 'No due date';
      break;
    default:
      title = 'Unknown goal type';
      dueDate = 'No due date';
  }

  return { title, dueDate };
};

export const getLatestConnection = (call: CallDto) => {
  return call?.connections
    ?.filter(connection => connection?.connectedAt)
    .sort((a, b) => b.connectedAt - a.connectedAt)
    ?.shift();
};

export const getCallProgressionStatus = (
  currentCall: CallDto | undefined,
  newCall: CallDto | undefined
) => {
  const hasNoSystemStatus = !currentCall?.systemStatus;
  const isCallFailed = currentCall?.status === 'FAILED';
  const isCallRinging = currentCall?.systemStatus === 'ringing';

  // Check if the call is truly completed by checking timeEnded
  // Don't rely solely on systemStatus as it might show 'completed' for one connection
  // while the call is still active with other participants
  const isCallCompleted = !!currentCall?.timeEnded;

  // Only 2 connections and latest connection is completed
  const latestConnection = getLatestConnection(currentCall);

  const isSecondConnectionCompleted =
    currentCall?.connections?.length === 2 &&
    !!latestConnection?.completedAt &&
    !!currentCall?.connections?.every(connection => connection?.connectedAt);
  // Check if there are any active connections (connected but not completed)
  const hasActiveConnections = currentCall?.connections?.some(
    conn => conn.connectedAt && !conn.completedAt
  );

  if (hasNoSystemStatus && isCallFailed) {
    return { type: 'failed' as const, text: 'Call Failed' };
  }

  if ((hasNoSystemStatus || isCallRinging) && !!newCall) {
    return { type: 'calling' as const, text: 'Calling...' };
  }

  if (isCallCompleted) {
    const duration = timeFormatter.millisToDurationString(
      currentCall?.duration
    );
    return { type: 'completed' as const, text: `Call Ended ${duration}` };
  }

  if (isSecondConnectionCompleted) {
    const duration = timeFormatter.millisToDurationString(
      latestConnection?.completedAt - latestConnection?.connectedAt
    );
    return { type: 'completed' as const, text: `Call Ended ${duration}` };
  }

  // If there are active connections, the call is in progress
  if (hasActiveConnections) {
    return { type: 'connected' as const, text: 'Call Connected' };
  }

  return null;
};

export function memberConnectionEngagementMeetsRequirement(
  memberConnection: CallConnectionDto,
  otherConnections: CallConnectionDto[],
  callEngagementLengthRequirement: number
): boolean {
  if (
    memberConnection.connectedAt == null ||
    memberConnection.completedAt == null
  )
    return false;

  const minMs = callEngagementLengthRequirement * 60_000;
  const mainStart = memberConnection.connectedAt;
  const mainEnd = memberConnection.completedAt;

  // create intervals for other connections that answered
  const intervals: Array<[number, number]> = [];
  for (const connection of otherConnections) {
    if (connection.connectedAt == null || connection.completedAt == null)
      continue;

    const start = Math.max(connection.connectedAt, mainStart);
    const end = Math.min(connection.completedAt, mainEnd);

    if (end > start) intervals.push([start, end]);
  }

  if (intervals.length === 0) return false;

  // 2) Sort + merge overlaps
  intervals.sort((a, b) => a[0] - b[0]);

  let total = 0;
  let [connectionStart, connectionEnd] = intervals[0];

  for (let i = 1; i < intervals.length; i++) {
    const [start, end] = intervals[i];

    if (start > connectionEnd) {
      total += connectionEnd - connectionStart;
      if (total >= minMs) return true;
      connectionStart = start;
      connectionEnd = end;
    } else {
      connectionEnd = Math.max(connectionEnd, end);
    }
  }

  total += connectionEnd - connectionStart;
  const minutes = total / 60_000;
  return minutes >= callEngagementLengthRequirement;
}
